import { useMemo, useState } from 'react';
import { cn } from '@jk-core/utils';
import styles from './SegmentButton.module.scss';

interface SegmentButtonProps {
  /**
   * 세그먼트 버튼의 너비를 지정합니다. 기본값은 'auto'입니다.
   * 만약 stretch가 true로 설정되면, 너비는 'auto'로 설정됩니다.
   */
  width?: string;
  /**
   * 세그먼트 버튼이 전체 너비를 차지하도록 설정합니다.
   * true로 설정하면 버튼이 부모 요소의 너비를 채웁니다.
   */
  stretch?: boolean;

  /**
   * 현재 세그먼트 버튼에서 선택된 인덱스입니다.
   * 값을 설정시, 해당 인덱스의 버튼이 선택된 상태로 표시됩니다.
   * 기본값은 0입니다.
   */
  selected?: number;
  /**
   * 세그먼트 버튼의 옵션을 설정합니다.
   * 각 옵션은 텍스트와 클릭 이벤트 핸들러를 포함합니다.
   * 예시:
   * ```ts
   * option: [
   *   { text: '오늘', onClick: () => setDate(new Date()) },
   *   { text: '내일', onClick: () => setDate(new Date(Date.now() + 24 * 60 * 60 * 1000)) },
   */
  option:Array<{ text: string; onClick: () => void }>;
}

/**
 * 세그먼트 버튼 컴포넌트는 여러 개의 버튼을 세그먼트 형태로 표시합니다.
 * 각 버튼은 클릭 시 지정된 함수를 실행합니다.
 */
export default function SegmentButton({ width, stretch, option, selected }: SegmentButtonProps) {
  const [index, setIndex] = useState(selected ?? 0);

  const selectedIndex = useMemo(() => selected ?? index, [index, selected]);

  return (
    <div
      className={styles.segment}
      style={{
        width: stretch ? '100%' : undefined,
        gridTemplateColumns: `repeat(${option.length},1fr)`,
      }}
    >
      <div
        className={styles['segment--selector']}
        style={{
          width: `${100 / option.length}%`,
          left: `${selectedIndex * 100 / option.length}%`,
        }}
      />
      {option.map((item, optionIndex) => (
        <button
          className={cn({
            [styles.button]: true,
            [styles['button--selected']]: optionIndex === selectedIndex,
          })}
          key={item.text}
          type="button"
          style={{ width: stretch ? 'auto' : (width || 'auto') }}
          onClick={() => {
            item.onClick();
            setIndex(optionIndex);
          }}
        >
          <div>
            {item.text}
          </div>
        </button>
      ))}
    </div>
  );
}